import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import PlaybookSectionsPanel from '@/components/playbooks/PlaybookSectionsPanel';
import PlaybookTransformEditorPanel from '@/components/playbooks/PlaybookTransformEditorPanel';
import PlaybookSchemaPreviewPanel from '@/components/playbooks/PlaybookSchemaPreviewPanel';
import { PlaybookTransformItem, PlaybookEventConfig, PlaybookMapping, PlaybookEnrichment, PlaybookExpression, PlaybookSpreading } from '@/types/playbook';
import { UnifiedSchema, SchemaType } from '@/types/schema';
import { fetchUnifiedSchema } from '@/services/schemaDefinitionApiService';
import { useNotifications } from '@/contexts/NotificationContext';
import { findParameterDataTypeInSchema } from '@/utils/schemaUtils';
import { useMediaQuery } from '@/hooks/useMediaQuery';
interface ValidationWarning {
  message: string;
  type: 'warning' | 'error';
}
import { PlaybookEditState } from '@/pages/labs/PlaybookEditPage';

export interface PlaybookEditorTabProps {
  state: PlaybookEditState;
  updateState: (updates: any) => void;
  validationWarnings: ValidationWarning[];
  currentSchemaView: 'input' | 'output' | 'yaml';
  setCurrentSchemaView: React.Dispatch<React.SetStateAction<'input' | 'output' | 'yaml'>>;
  currentYamlView: 'playbook' | 'connection';
  setCurrentYamlView: React.Dispatch<React.SetStateAction<'playbook' | 'connection'>>;
  selection: EditorSelection; // Add selection prop
  setSelection: React.Dispatch<React.SetStateAction<EditorSelection>>; // Add setSelection prop
  // Labs UI state props
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  viewMode: PlaybookViewMode;
  setViewMode: (mode: PlaybookViewMode) => void;
  rightPanelWidth: number;
  setRightPanelWidth: React.Dispatch<React.SetStateAction<number>>;
  isRightPanelCollapsed: boolean;
  setIsRightPanelCollapsed: (collapsed: boolean) => void;
}

export type SelectionType = 'global' | 'event' | 'connection'; // Add 'connection' type

export interface EditorSelection {
  type: SelectionType;
  eventId?: string;
  transformId?: string;
  // No specific ID needed for connection, as it's a singular section
}

import { PlaybookViewMode } from '@/components/playbooks/transforms-list/PlaybookTransformList';

const PlaybookEditorTab: React.FC<PlaybookEditorTabProps> = ({
  state,
  updateState,
  validationWarnings,
  currentSchemaView,
  setCurrentSchemaView,
  currentYamlView,
  setCurrentYamlView,
  selection, // Destructure selection from props
  setSelection, // Destructure setSelection from props
  searchTerm, // Destructure from props
  setSearchTerm, // Destructure from props
  viewMode, // Destructure from props
  setViewMode, // Destructure from props
  rightPanelWidth, // Destructure from props
  setRightPanelWidth, // Destructure from props
  isRightPanelCollapsed, // Destructure from props
  setIsRightPanelCollapsed, // Destructure from props
}) => {
  const { showNotification } = useNotifications();
  const { allowUndefinedTransforms, connectionJSON } = state; // Destructure the new prop
  const [inputSchema, setInputSchema] = useState<UnifiedSchema | null>(null);
  const [outputSchema, setOutputSchema] = useState<UnifiedSchema | null>(null);
  
  // Responsive behavior
  const isSmallScreen = useMediaQuery('(max-width: 1599px)'); // Below 1600px
  const isMediumScreen = useMediaQuery('(min-width: 1600px) and (max-width: 2159px)'); // 1600px to 2159px
  const isLargeScreen = useMediaQuery('(min-width: 2160px) and (max-width: 2879px)'); // 2160px to 2879px
  const isExtraLargeScreen = useMediaQuery('(min-width: 2880px)'); // 2880px and above
  
  // Calculate responsive width based on screen size with smooth scaling
  const calculateResponsiveWidth = () => {
    if (isExtraLargeScreen) return Math.min(800, Math.max(640, window.innerWidth * 0.2)); // 20% of screen width, clamped between 640px and 800px
    if (isLargeScreen) return Math.min(640, Math.max(480, window.innerWidth * 0.25)); // 25% of screen width, clamped
    if (isMediumScreen) return Math.min(480, Math.max(360, window.innerWidth * 0.22)); // 22% of screen width, clamped
    return Math.min(360, Math.max(280, window.innerWidth * 0.2)); // 20% of screen width for small screens
  };

  // Update panel width when screen size changes or screen size category changes
  useEffect(() => {
    const handleResize = () => {
      const newWidth = calculateResponsiveWidth();
      if (newWidth !== rightPanelWidth) {
        setRightPanelWidth(newWidth);
      }
    };

    // Set initial width on mount
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isExtraLargeScreen, isLargeScreen, isMediumScreen, rightPanelWidth, setRightPanelWidth]); // Dependencies are the media query states
  
  // Determine which schema to use as baseline for events
  const baselineSchema = useMemo(() => {
    if (state.baselineSchema === 'input') {
      return inputSchema;
    } else if (state.baselineSchema === 'output') {
      return outputSchema;
    } else {
      // Default fallback based on playbook type
      if (state.type === 'pipeline') {
        return inputSchema;
      } else if (state.type === 'integration') {
        return outputSchema;
      }
    }
    return null;
  }, [state.baselineSchema, state.type, inputSchema, outputSchema]);

  // Determine the effective baseline schema type for UI display
  const effectiveBaselineSchemaType = useMemo(() => {
    if (state.baselineSchema) {
      return state.baselineSchema;
    }
    // Default fallback based on playbook type
    if (state.type === 'pipeline') {
      return 'input';
    } else if (state.type === 'integration') {
      return 'output';
    }
    return undefined;
  }, [state.baselineSchema, state.type]);

  // Fetch input and output schemas
  useEffect(() => {
    const loadSchemas = async () => {
      let inputSchemaLoaded = false;
      let outputSchemaLoaded = false;
      
      let expectedInputSchemaType: string | undefined;
      let expectedOutputSchemaType: string | undefined;
      
      if (state.type === 'pipeline') {
        expectedInputSchemaType = 'customer_schema';
        expectedOutputSchemaType = 'pipeline_schema';
      } else if (state.type === 'integration') {
        expectedInputSchemaType = 'pipeline_schema';
        expectedOutputSchemaType = 'vendor_schema';
      }

      if (state.input_schema_id && expectedInputSchemaType) {
        try {
          const schema = await fetchUnifiedSchema(state.input_schema_id, expectedInputSchemaType as SchemaType);
          if (schema) {
            setInputSchema(schema);
            inputSchemaLoaded = true;
          } else {
            showNotification('error', `Failed to load input schema (${expectedInputSchemaType}) - received null data.`);
            setInputSchema(null);
          }
        } catch (error) {
          console.error(`Failed to fetch input schema (${expectedInputSchemaType}):`, error);
          showNotification('error', `Failed to load input schema (${expectedInputSchemaType}). Using mock data instead.`);
          inputSchemaLoaded = true;
        }
      } else {
        setInputSchema(null);
      }

      if (state.output_schema_id && expectedOutputSchemaType) {
        try {
          const schema = await fetchUnifiedSchema(state.output_schema_id, expectedOutputSchemaType as SchemaType);
          if (schema) {
            setOutputSchema(schema);
            outputSchemaLoaded = true;
          } else {
            showNotification('error', `Failed to load output schema (${expectedOutputSchemaType}) - received null data.`);
            setOutputSchema(null);
          }
        } catch (error) {
          console.error(`Failed to fetch output schema (${expectedOutputSchemaType}):`, error);
          showNotification('error', `Failed to load output schema (${expectedOutputSchemaType}). Using mock data instead.`);
          outputSchemaLoaded = true;
        }
      } else {
        setOutputSchema(null);
      }
      
      // If both schemas failed to load, show a more detailed error
      if (state.input_schema_id && state.output_schema_id && !inputSchemaLoaded && !outputSchemaLoaded) {
        showNotification('error', 'Failed to load both input and output schemas. Please check your network connection or try again later.');
      }
    };

    loadSchemas();
  }, [state.input_schema_id, state.output_schema_id, state.type, showNotification]);

  // Auto-initialize baseline schema events as configured events
  useEffect(() => {
    if (!baselineSchema?.events || baselineSchema.events.length === 0) {
      return;
    }

    // Check if we need to initialize events from the baseline schema
    const baselineEvents = baselineSchema.events;
    const existingEventCodeNames = new Set(state.uiEventConfigs.map(e => e.codeName));
    const eventsToAdd: PlaybookEventConfig[] = [];

    baselineEvents.forEach(schemaEvent => {
      // Only add if we don't already have a configured event with this codeName
      if (!existingEventCodeNames.has(schemaEvent.codeName)) {
        const newEvent: PlaybookEventConfig = {
          id: `event_${schemaEvent.codeName}_${Date.now()}`,
          name: schemaEvent.name,
          codeName: schemaEvent.codeName,
          description: schemaEvent.description,
          transformItemIds: [],
        };
        eventsToAdd.push(newEvent);
      }
    });

    // Add the new events if any
    if (eventsToAdd.length > 0) {
      updateState({
        uiEventConfigs: [...state.uiEventConfigs, ...eventsToAdd]
      });
    }
  }, [baselineSchema?.events, state.uiEventConfigs, updateState]);

  // Enrich transforms with schema-derived properties and event counts
  const enrichedTransforms = useMemo(() => {
    
    const totalEventCount = state.uiEventConfigs.length;

    const enriched = state.uiTransforms.map(transform => {
      const eventUsageCount = transform.eventIds?.length || 0;
      let isPII: boolean | undefined = undefined;
      let isRequired: boolean | undefined = undefined;
      let mappingType: PlaybookTransformItem['mappingType'] = transform.mappingType; // Start with existing mappingType
      let inputType: string | undefined = undefined;
      let outputType: string | undefined = undefined;
      let __meta = transform.__meta; // Start with existing __meta

      if (transform.type === 'mapping') {
        const mappingContent = transform.content as PlaybookMapping;
        inputType = mappingContent.inputKey ? findParameterDataTypeInSchema(inputSchema, mappingContent.inputKey) : undefined;
        outputType = mappingContent.outputKey ? findParameterDataTypeInSchema(outputSchema, mappingContent.outputKey) : undefined;

        const inputParam = inputSchema?.parameters.find(p => p.fullPath === mappingContent.inputKey || p.path === mappingContent.inputKey);
        const outputParam = outputSchema?.parameters.find(p => p.fullPath === mappingContent.outputKey || p.path === mappingContent.outputKey);

        isPII = outputParam?.isPii;
        // isRequired is not available on Parameter type, so omitting for now
        
        // Only re-evaluate mappingType if it's not already 'ai' or if it's a new transform
        if (mappingType !== 'ai' && !transform.isNew) {
          if (mappingContent.transforms && mappingContent.transforms.length > 0) {
            mappingType = 'direct'; 
          } else if (inputParam && outputParam) {
            mappingType = 'direct';
          } else {
            mappingType = 'manual';
          }
        } else if (transform.isNew) {
          // For new transforms, default to manual if not explicitly set by AI
          if (!mappingType) {
            mappingType = 'manual';
          }
        }
      } else if (transform.type === 'enrichment') {
        const enrichmentContent = transform.content as PlaybookEnrichment;
        
        // Convert enrichment to a mapping with undefined inputKey
        const newTransform: PlaybookTransformItem = {
          ...transform,
          type: 'mapping', // Change type to mapping
          content: {
            outputKey: enrichmentContent.outputKey,
            inputKey: undefined, // Explicitly set inputKey to undefined for direct assign mapping without input
            // Assign value to defaultString or defaultInt based on type
            defaultString: typeof enrichmentContent.value === 'string' ? enrichmentContent.value : undefined,
            defaultInt: typeof enrichmentContent.value === 'number' ? enrichmentContent.value : undefined,
            transforms: [], // No expression, so transforms array is empty
          } as PlaybookMapping,
          mappingType: 'DirectAssign', // This will be a direct mapping
          inputType: undefined, // No input type
          outputType: enrichmentContent.outputKey ? findParameterDataTypeInSchema(outputSchema, enrichmentContent.outputKey) : undefined,
        };

        // Re-evaluate isPII based on the new mapping's outputKey
        const outputParam = outputSchema?.parameters.find(p => p.fullPath === newTransform.content!.outputKey || p.path === newTransform.content!.outputKey);
        newTransform.isPII = outputParam?.isPii;

        return newTransform; // Return the converted transform
      } else if (transform.type === 'expression') {
        const expressionContent = transform.content as PlaybookExpression;
        if (expressionContent.declaredOutputProperties && expressionContent.declaredOutputProperties.length > 0) {
          // For expressions, outputType might be derived from declaredOutputProperties
          // Assuming the first declared output property's dataType for simplicity if there's a primary output.
          // More complex logic might be needed if multiple outputs or dynamic outputs.
          outputType = expressionContent.declaredOutputProperties[0].dataType;
        }
      } else if (transform.type === 'spreading') {
        const spreadingContent = transform.content as PlaybookSpreading;
        if (spreadingContent.inferredOutputProperties && spreadingContent.inferredOutputProperties.length > 0) {
          // For spreading, outputType might be derived from inferredOutputProperties
          outputType = spreadingContent.inferredOutputProperties[0].dataType;
        }
      }
      // For other transform types (filter), isPII/isRequired/mappingType/types might not be directly applicable
      // or require more complex logic based on declared/inferred output properties.
      // For now, leave them as undefined.

      // Get event names for the transform
      const eventNames = transform.eventIds
        ? state.uiEventConfigs
            .filter(event => transform.eventIds?.includes(event.id))
            .map(event => event.name)
        : [];

      return {
        ...transform,
        eventUsageCount,
        totalEventCount,
        eventNames, // Add eventNames
        isPII,
        isRequired,
        mappingType, // Use the potentially updated mappingType
        inputType, // Add inputType
        outputType, // Add outputType
        __meta, // Ensure __meta is carried over
      };
    });
    
    return enriched;
  }, [state.uiTransforms, state.uiEventConfigs, inputSchema, outputSchema, findParameterDataTypeInSchema]);

  // Get selected event if any
  const selectedEvent = useMemo(() => {
    if (selection.type === 'event' && selection.eventId) {
      return state.uiEventConfigs.find(event => event.id === selection.eventId);
    }
    return null;
  }, [selection, state.uiEventConfigs]);

  // Get selected transform if any (from enriched transforms)
  const selectedTransform = useMemo(() => {
    if (selection.transformId) {
      return enrichedTransforms.find(transform => transform.id === selection.transformId);
    }
    return null;
  }, [selection.transformId, enrichedTransforms]);

  const handleAddEvent = (name: string, codeName: string, description?: string) => {
    const newEvent: PlaybookEventConfig = {
      id: `event_${Date.now()}`,
      name,
      codeName,
      description,
      transformItemIds: [],
    };

    updateState({
      uiEventConfigs: [...state.uiEventConfigs, newEvent]
    });
  };

  const handleUpdateEvent = (eventId: string, updates: Partial<PlaybookEventConfig>) => {
    const updatedEvents = state.uiEventConfigs.map(event =>
      event.id === eventId ? { ...event, ...updates } : event
    );
    
    updateState({
      uiEventConfigs: updatedEvents
    });
  };

  const handleDeleteEvent = (eventId: string) => {
    // Remove the event
    const updatedEvents = state.uiEventConfigs.filter(event => event.id !== eventId);
    
    // Remove transforms that were only for this event
    const updatedTransforms = state.uiTransforms.filter(transform => {
      if (transform.appliesToScope === 'eventSpecific' && transform.eventIds) {
        const remainingEventIds = transform.eventIds.filter(id => id !== eventId);
        return remainingEventIds.length > 0;
      }
      return true;
    }).map(transform => {
      if (transform.appliesToScope === 'eventSpecific' && transform.eventIds) {
        return {
          ...transform,
          eventIds: transform.eventIds.filter(id => id !== eventId)
        };
      }
      return transform;
    });

    updateState({
      uiEventConfigs: updatedEvents,
      uiTransforms: updatedTransforms
    });

    // Reset selection if the deleted event was selected
    if (selection.type === 'event' && selection.eventId === eventId) {
      setSelection({ type: 'global' });
    }
  };

  const handleAddTransforms = (transformsToAdd: PlaybookTransformItem[]) => {
    const newTransforms = [...state.uiTransforms, ...transformsToAdd];
    
    updateState({
      uiTransforms: newTransforms
    });

    // Automatically select the newly added transform and adjust section based on its scope
    if (transformsToAdd.length > 0) {
      const addedTransform = transformsToAdd[0];
      if (addedTransform.appliesToScope === 'eventSpecific' && addedTransform.eventIds && addedTransform.eventIds.length > 0) {
        // If event-specific, stay on the current event section and select the transform
        setSelection({ type: 'event', eventId: addedTransform.eventIds[0], transformId: addedTransform.id });
      } else {
        // If global (or no eventId for event-specific), switch to global section and select the transform
        setSelection({ type: 'global', transformId: addedTransform.id });
      }
    }
  };



  const handleUpdateConnection = (connection: object) => {
    console.log('PlaybookEditorTab: handleUpdateConnection called with:', connection);
    
    // NOTE: This handles TWO different types of updates:
    // 1. Connection parameter DEFINITIONS (from ConnectionEditorTab) -> updates connectionJSON (connection.yaml)
    // 2. Connection parameter VALUES (from ConnectionParametersForm) -> updates params (playbook.yaml)
    
    const connectionUpdate = connection as any;
    
    if (connectionUpdate.parameterValues) {
      // This is a parameter VALUES update (for playbook.yaml)
      console.log('PlaybookEditorTab: Updating playbook parameter values:', connectionUpdate.parameterValues);
      updateState({
        params: connectionUpdate.parameterValues
      });
      console.log('PlaybookEditorTab: updateState called for params (playbook parameter values)');
    } else {
      // This is a connection DEFINITION update (for connection.yaml)
      console.log('PlaybookEditorTab: Updating connection definitions (connectionJSON)');
      updateState({
        connectionJSON: connection
      });
      console.log('PlaybookEditorTab: updateState called for connectionJSON (connection definitions)');
    }
  };

  const handleUpdateTransform = (transformId: string, updates: Partial<PlaybookTransformItem>) => {
    const updatedTransforms = state.uiTransforms.map(transform => {
      if (transform.id === transformId) {
        let newUpdates = { ...updates };

        // Handle scope change logic
        if (updates.appliesToScope !== undefined && updates.appliesToScope !== transform.appliesToScope) {
          if (updates.appliesToScope === 'global') {
            // If changing to global, clear eventIds
            newUpdates.eventIds = undefined;
          } else if (updates.appliesToScope === 'eventSpecific') {
            // If changing to eventSpecific and no specific eventIds are provided,
            // assign to all existing events.
            // This covers the case where a global transform is downgraded.
            if (updates.eventIds === undefined) {
              newUpdates.eventIds = state.uiEventConfigs.map(event => event.id);
            }
          }
        }

        // If eventIds are explicitly updated, ensure appliesToScope is 'eventSpecific'
        if (updates.eventIds !== undefined && newUpdates.appliesToScope === undefined) {
          newUpdates.appliesToScope = 'eventSpecific';
        }

        return { ...transform, ...newUpdates };
      }
      return transform;
    });
    
    updateState({
      uiTransforms: updatedTransforms
    });
  };

  const handleBatchUpdateTransforms = (batchUpdates: { [transformId: string]: Partial<PlaybookTransformItem> }) => {
    
    const updatedTransforms = state.uiTransforms.map(transform => {
      const updates = batchUpdates[transform.id];
      if (updates) {
        let newUpdates = { ...updates };

        // Handle scope change logic
        if (updates.appliesToScope !== undefined && updates.appliesToScope !== transform.appliesToScope) {
          if (updates.appliesToScope === 'global') {
            // If changing to global, clear eventIds
            newUpdates.eventIds = undefined;
          } else if (updates.appliesToScope === 'eventSpecific') {
            // If changing to eventSpecific and no specific eventIds are provided,
            // assign to all existing events.
            // This covers the case where a global transform is downgraded.
            if (updates.eventIds === undefined) {
              newUpdates.eventIds = state.uiEventConfigs.map(event => event.id);
            }
          }
        }

        // If eventIds are explicitly updated, ensure appliesToScope is 'eventSpecific'
        if (updates.eventIds !== undefined && newUpdates.appliesToScope === undefined) {
          newUpdates.appliesToScope = 'eventSpecific';
        }

        console.log(`🔄 Applying batch update for transform ${transform.id}:`, newUpdates);
        return { ...transform, ...newUpdates };
      }
      return transform;
    });
    
    updateState({
      uiTransforms: updatedTransforms
    });
  };

  const handleDeleteTransform = (transformId: string) => {
    const updatedTransforms = state.uiTransforms.filter(transform => transform.id !== transformId);
    
    updateState({
      uiTransforms: updatedTransforms
    });

    // Reset transform selection if the deleted transform was selected
    if (selection.transformId === transformId) {
      setSelection(prev => ({ ...prev, transformId: undefined }));
    }
  };

  const handleToggleRightPanel = () => {
    setIsRightPanelCollapsed(!isRightPanelCollapsed);
  };


  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="h-full flex"
    >
      {/* Left Panel: Sections */}
      <div className={`
        ${isSmallScreen ? 'w-56' : isMediumScreen ? 'w-64' : 'w-80'}
        border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0 h-full flex flex-col
      `}>
        <PlaybookSectionsPanel
          events={state.uiEventConfigs}
          transforms={state.uiTransforms}
          selection={selection}
          onSelectionChange={setSelection}
          onAddEvent={handleAddEvent}
          onDeleteEvent={handleDeleteEvent}
          baselineSchemaEvents={baselineSchema?.events || []}
          allowUndefinedTransforms={allowUndefinedTransforms}
          connectionJSON={connectionJSON} // Pass connectionJSON
          isMobile={isSmallScreen} // Use isSmallScreen for mobile-like behavior
          isTablet={isMediumScreen} // Use isMediumScreen for tablet-like behavior
          isDesktopMedium={isLargeScreen} // Use isLargeScreen for desktop-medium-like behavior
          validationWarnings={validationWarnings}
          playbookType={state.type} // Add missing playbookType prop
        />
      </div>

      {/* Center Panel: Transform Editor */}
      <div className="flex-1 bg-gray-50 dark:bg-gray-900 flex flex-col overflow-hidden">
        <div className="max-w-[1800px] min-w-[800px] mx-auto w-full flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <PlaybookTransformEditorPanel
              selection={selection}
              selectedEvent={selectedEvent || null}
              selectedTransform={selectedTransform || null}
              transforms={enrichedTransforms} // Pass enriched transforms
              onAddTransforms={handleAddTransforms}
              onUpdateTransform={handleUpdateTransform}
              onBatchUpdateTransforms={handleBatchUpdateTransforms}
              onDeleteTransform={handleDeleteTransform}
              onUpdateEvent={handleUpdateEvent}
              onTransformSelect={(transformId: string) =>
                setSelection(prev => ({ ...prev, transformId }))
              }
              onSelectionChange={setSelection} // Pass setSelection
              inputSchema={inputSchema} // Pass input schema
              outputSchema={outputSchema} // Pass output schema
              allEvents={baselineSchema?.events?.map(event => ({
                id: event.id,
                name: event.name,
                codeName: event.codeName,
                description: event.description || '',
                parameterFullPaths: event.parameterFullPaths || [],
                isNew: event.isNew,
              })) || []}
              uiEventConfigs={state.uiEventConfigs}
              allUiTransforms={state.uiTransforms}
              allowUndefinedTransforms={allowUndefinedTransforms}
              baselineSchema={
                state.baselineSchema || // Use explicit baseline if set
                (state.type === 'pipeline' ? 'input' : // Fallback for pipeline
                state.type === 'integration' ? 'output' : undefined) // Fallback for integration
              }
              searchTerm={searchTerm} // Pass searchTerm
              setSearchTerm={setSearchTerm} // Pass setSearchTerm
              viewMode={viewMode} // Pass viewMode
              setViewMode={setViewMode} // Pass setViewMode
              connectionJSON={connectionJSON} // Pass connectionJSON
              connectionValues={state.params} // Pass saved connection parameter values
              onUpdateConnection={handleUpdateConnection} // Pass connection update handler
            />
          </div>
        </div>
      </div>

      {/* Right Panel: Schema Preview */}
      <div
        className={`border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 h-full flex flex-col flex-shrink-0`}
        style={
          isRightPanelCollapsed
            ? { width: '0px', overflow: 'visible', position: 'relative' }
            : { width: `${rightPanelWidth}px`, overflow: 'visible', position: 'relative' }
        }
      >
        <PlaybookSchemaPreviewPanel
          state={state}
          inputSchemaId={state.input_schema_id}
          outputSchemaId={state.output_schema_id}
          transforms={enrichedTransforms}
          events={state.uiEventConfigs}
          selection={selection}
          baselineSchemaEvents={baselineSchema?.events || []}
          width={rightPanelWidth}
          setWidth={setRightPanelWidth}
          baselineSchema={effectiveBaselineSchemaType}
          inputSchema={inputSchema}
          outputSchema={outputSchema}
          isDesktopMedium={isMediumScreen} // Pass new prop for medium screen
          isLargeDesktop={isExtraLargeScreen} // Pass new prop for extra large screen (where it's resizable)
          isCollapsed={isRightPanelCollapsed}
          onToggleCollapse={handleToggleRightPanel}
            onTransformSelect={(transformId: string) => {
              console.log('PlaybookEditorTab: onTransformSelect called with transformId:', transformId);
              const transformToSelect = state.uiTransforms.find(t => t.id === transformId);

              if (transformToSelect) {
                setSelection(prev => {
                  let newSelection: EditorSelection = { ...prev };

                  // Always switch to 'global' section if a transform is selected from the schema preview
                  // This simplifies the behavior as requested, treating connection properties as global transforms
                  if (newSelection.type !== 'global') {
                    newSelection = { type: 'global' };
                  }
                  
                  // Set the transformId in the new selection
                  newSelection.transformId = transformId;
                  return newSelection;
                });
              } else {
                console.warn('PlaybookEditorTab: Transform not found for ID:', transformId);
              }
            }}
          onAddTransforms={handleAddTransforms} // Pass the transform creation handler
          currentSchemaView={currentSchemaView} // Pass down
          setCurrentSchemaView={setCurrentSchemaView} // Pass down
          currentYamlView={currentYamlView} // Pass down
          setCurrentYamlView={setCurrentYamlView} // Pass down
        />
        
        {/* Toggle Button for Screens where it's not Extra Large Desktop - positioned outside the panel */}
        {!isExtraLargeScreen && (
          <button
            className="absolute left-[-26px] bottom-[8%] z-30 p-1 bg-white dark:bg-gray-700 border-t border-l border-b border-gray-300 dark:border-gray-900 rounded-l-md shadow-xs hover:bg-gray-100 dark:hover:bg-gray-600"
            onClick={handleToggleRightPanel}
            title={isRightPanelCollapsed ? "Expand Schema Panel" : "Collapse Schema Panel"}
          >
            {isRightPanelCollapsed ? <ChevronLeft size={16} className="text-gray-600 dark:text-gray-300" /> : <ChevronRight size={16} className="text-gray-600 dark:text-gray-300" />}
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default PlaybookEditorTab;
